import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { addDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "./firebaseConfig";

const FEATURES = [
  "Custom aliases (e.g. blinkz.us/-yourname)",
  "Click analytics on all your links",
  "Full link history in your dashboard",
];

export default function UpgradeModal({ open, onClose, user, onSignIn }) {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!user) {
      onClose();
      onSignIn();
      return;
    }
    setLoading(true);
    try {
      const priceId = process.env.REACT_APP_STRIPE_PRICE_ID;
      if (!priceId) {
        alert("Stripe is not configured yet. Set REACT_APP_STRIPE_PRICE_ID in .env");
        setLoading(false);
        return;
      }
      const docRef = await addDoc(
        collection(db, "customers", user.uid, "checkout_sessions"),
        {
          price: priceId,
          success_url: window.location.origin + "?upgraded=true",
          cancel_url: window.location.origin,
          allow_promotion_codes: true,
        }
      );
      onSnapshot(docRef, (snap) => {
        const { error, url } = snap.data() || {};
        if (error) {
          alert(error.message);
          setLoading(false);
        }
        if (url) window.location.assign(url);
      });
    } catch (e) {
      console.error(e);
      alert("Could not start checkout. Make sure the Firebase Stripe Extension is installed.");
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 5, p: 1, maxWidth: 400 } }}
    >
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <WorkspacePremiumRoundedIcon sx={{ color: "#fff", fontSize: 28 }} />
          </Box>
          <Typography variant="h6" fontWeight={800} textAlign="center">
            Upgrade to Blinkz Pro
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Unlock the full shortener experience for{" "}
            <Box component="span" fontWeight={700} color="text.primary">$13.99/year</Box>.
          </Typography>
        </Box>

        <List dense disablePadding>
          {FEATURES.map((f) => (
            <ListItem key={f} disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleRoundedIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={f}
                primaryTypographyProps={{ fontSize: "0.9rem" }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: "column", gap: 1 }}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleUpgrade}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: 50, py: 1.25, fontWeight: 700 }}
        >
          {loading ? "Preparing checkout…" : "Upgrade now · $13.99/year"}
        </Button>
        <Button
          variant="text"
          fullWidth
          onClick={onClose}
          sx={{ borderRadius: 50, color: "text.secondary" }}
        >
          Maybe later
        </Button>
      </DialogActions>
    </Dialog>
  );
}
